<template>
  <v-dialog v-model="internalOpen" persistent max-width="950px">
    <v-card class="rounded-xl overflow-hidden bg-white" outlined elevation="3">
      <!-- Encabezado Neutro y Limpio Corporativo (Sin Degradados) -->
      <div class="px-5 py-4 d-flex align-center justify-space-between border-light-bottom bg-white">
        <div class="d-flex align-center">
          <v-avatar color="grey lighten-4" size="40" class="mr-3">
            <v-icon color="green darken-2">mdi-file-excel</v-icon>
          </v-avatar>
          <div>
            <h2 class="text-subtitle-1 font-weight-bold grey--text text--darken-4 mb-0">
              Importación Masiva de Precios desde Excel
            </h2>
            <span class="text-caption grey--text text--darken-2">
              {{ isSpecialPrice ? 'Configuración de Precios Especiales por Productor' : 'Configuración de Precios Estándar General' }}
            </span>
          </div>
        </div>
        <div class="d-flex align-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="green darken-2"
                outlined
                small
                v-bind="attrs"
                v-on="on"
                class="rounded-lg text-none font-weight-bold mr-2"
                @click="downloadTemplate"
                :loading="generatingTemplate"
              >
                <v-icon left small>mdi-download</v-icon>
                Descargar Plantilla
              </v-btn>
            </template>
            <span>Generar archivo Excel preconfigurado con IDs de productores y fechas</span>
          </v-tooltip>
          <v-btn icon small @click="closeModal" color="grey darken-2">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <v-card-text class="pa-5 bg-slate-50">
        <!-- Bloque Informativo de Parámetros Activos -->
        <v-card outlined class="pa-3 mb-4 rounded-lg bg-white border-light" elevation="0">
          <div class="d-flex align-center justify-space-between flex-wrap text-caption">
            <div>
              <span class="grey--text text--darken-2 font-weight-medium">Rango de Fechas: </span>
              <span class="font-weight-bold grey--text text--darken-4">
                {{ formatDateDMY(dateBegin) }} al {{ formatDateDMY(dateEnd) }} ({{ rangeDays.length }} días)
              </span>
            </div>
            <div class="mt-1 mt-sm-0">
              <span class="grey--text text--darken-2 font-weight-medium">Tipo: </span>
              <v-chip x-small :color="isSpecialPrice ? 'purple lighten-5' : 'blue lighten-5'" class="font-weight-bold" :class="isSpecialPrice ? 'purple--text text--darken-3' : 'primary--text'">
                {{ isSpecialPrice ? 'PRECIO ESPECIAL (PRODUCTORES)' : 'PRECIO ESTÁNDAR GENERAL' }}
              </v-chip>
            </div>
            <div class="mt-1 mt-sm-0" v-if="isSpecialPrice">
              <span class="grey--text text--darken-2 font-weight-medium">Productores Seleccionados: </span>
              <span class="font-weight-bold primary--text">{{ selectedProducers.length }}</span>
            </div>
          </div>
        </v-card>

        <!-- Instrucción Rápida -->
        <v-alert
          dense
          outlined
          type="info"
          class="rounded-lg mb-4 text-caption font-weight-medium"
        >
          <template v-if="isSpecialPrice">
            <b>Paso 1:</b> Descargue la plantilla Excel. Esta incluirá las filas con los <b>IDs de productores seleccionados</b> y las columnas por cada día del rango.<br />
            <b>Paso 2:</b> Ingrese los precios por calibre y día en el archivo.<br />
            <b>Paso 3:</b> Suba el archivo aquí para cargar todos los precios automáticamente en la matriz.
          </template>
          <template v-else>
            Descargue la plantilla con las columnas de fechas del rango, complete los precios de cada calibre y cargue el archivo.
          </template>
        </v-alert>

        <!-- Zona de Carga de Archivo Excel -->
        <div
          class="upload-dropzone pa-5 rounded-xl border-dashed bg-white text-center cursor-pointer mb-4"
          :class="{ 'file-loaded': !!uploadedFile }"
          @click="triggerFileInput"
        >
          <input
            type="file"
            ref="fileInputRef"
            accept=".xlsx,.xls"
            class="d-none"
            @change="onFileSelected"
          />

          <div v-if="!uploadedFile">
            <v-avatar color="blue lighten-5" size="52" class="mb-2">
              <v-icon color="primary" large>mdi-cloud-upload-outline</v-icon>
            </v-avatar>
            <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4">
              Haga clic aquí para seleccionar el archivo Excel (.xlsx)
            </div>
            <div class="text-caption grey--text text--darken-1">
              O arrastre y suelte su archivo de plantilla completada
            </div>
          </div>

          <div v-else class="d-flex align-center justify-space-between flex-wrap">
            <div class="d-flex align-center">
              <v-avatar color="green lighten-5" size="44" class="mr-3">
                <v-icon color="green darken-2">mdi-file-check-outline</v-icon>
              </v-avatar>
              <div class="text-left">
                <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-truncate" style="max-width: 450px;">
                  {{ uploadedFile.name }}
                </div>
                <div class="text-caption grey--text text--darken-1">
                  {{ formatFileSize(uploadedFile.size) }}
                </div>
              </div>
            </div>

            <div class="d-flex align-center mt-2 mt-sm-0">
              <v-btn
                small
                text
                color="error"
                class="rounded-lg text-none font-weight-medium"
                @click.stop="clearFile"
              >
                <v-icon left small>mdi-delete-outline</v-icon>
                Quitar archivo
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Indicador de Procesamiento -->
        <div v-if="parsingFile" class="py-4 text-center">
          <v-progress-circular indeterminate size="28" color="primary" class="mr-2"></v-progress-circular>
          <span class="text-caption font-weight-medium grey--text text--darken-3">
            Analizando estructura del archivo Excel y validando IDs...
          </span>
        </div>

        <!-- Vista Previa / Resultados de Validación -->
        <div v-else-if="parseResult" class="mb-3">
          <!-- Alerta de Errores si los hay -->
          <v-alert
            v-if="parseResult.errors && parseResult.errors.length > 0"
            dense
            type="error"
            outlined
            class="rounded-lg mb-3 text-caption"
          >
            <div class="font-weight-bold mb-1">
              Se detectaron {{ parseResult.errors.length }} observaciones en el archivo:
            </div>
            <ul class="pl-4 ma-0">
              <li v-for="(err, idx) in parseResult.errors.slice(0, 5)" :key="'err-' + idx">
                {{ err }}
              </li>
              <li v-if="parseResult.errors.length > 5">
                ... y {{ parseResult.errors.length - 5 }} errores adicionales.
              </li>
            </ul>
          </v-alert>

          <!-- Tarjeta de Resumen Exitoso -->
          <v-card outlined class="pa-4 rounded-xl bg-white border-light" elevation="0">
            <div class="d-flex align-center justify-space-between mb-3 flex-wrap">
              <div class="d-flex align-center">
                <v-icon color="green darken-2" class="mr-2">mdi-check-circle-outline</v-icon>
                <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4">
                  Datos Validados y Listos para Cargar
                </span>
              </div>
              <div class="d-flex align-center flex-wrap">
                <v-chip small color="green lighten-5" class="green--text text--darken-3 font-weight-bold mr-2">
                  {{ parseResult.validRowsCount }} filas de calibres
                </v-chip>
                <v-chip v-if="isSpecialPrice" small color="purple lighten-5" class="purple--text text--darken-3 font-weight-bold mr-2">
                  {{ parseResult.producers.length }} productores detectados
                </v-chip>
                <v-chip small color="blue lighten-5" class="primary--text font-weight-bold">
                  {{ parseResult.dateColumns.length }} fechas en rango
                </v-chip>
              </div>
            </div>

            <!-- Tabla Resumen de Productores a Cargar -->
            <div v-if="isSpecialPrice && parseResult.producers.length > 0" class="producer-preview-list">
              <div class="text-caption font-weight-bold grey--text text--darken-3 mb-2">
                Productores identificados en el archivo:
              </div>
              <v-simple-table dense class="border-rounded border-light">
                <template v-slot:default>
                  <thead>
                    <tr class="bg-slate-50">
                      <th class="text-center font-weight-bold text-caption" style="width: 100px;">ID</th>
                      <th class="text-left font-weight-bold text-caption" style="width: 130px;">CÓDIGO</th>
                      <th class="text-left font-weight-bold text-caption">PRODUCTOR</th>
                      <th class="text-center font-weight-bold text-caption" style="width: 120px;">CALIBRES</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="p in parseResult.producers" :key="'preview-p-' + p.PrdID">
                      <td class="text-center font-weight-bold text-caption">{{ p.PrdID }}</td>
                      <td class="text-left text-caption">{{ p.PrdCardCode || '-' }}</td>
                      <td class="text-left text-caption font-weight-medium">{{ p.PrdCardName }}</td>
                      <td class="text-center text-caption font-weight-bold primary--text">
                        {{ getProducerCalibersCount(p.PrdID) }} calibres
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-card>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Botones de Acción Inferiores -->
      <v-card-actions class="pa-4 bg-white d-flex justify-space-between">
        <v-btn
          outlined
          color="grey darken-2"
          class="rounded-lg px-4 text-none font-weight-medium"
          @click="closeModal"
        >
          Cancelar
        </v-btn>

        <v-btn
          color="primary"
          class="rounded-lg px-5 text-none font-weight-bold white--text elevation-0"
          :disabled="!canApplyImport"
          @click="applyImport"
        >
          <v-icon left small>mdi-table-arrow-up</v-icon>
          Cargar Datos en la Matriz
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const ExcelJS = require("exceljs");

export default {
  name: "MangoPriceImportModal",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    isSpecialPrice: {
      type: Boolean,
      default: false,
    },
    dateBegin: {
      type: String,
      default: "",
    },
    dateEnd: {
      type: String,
      default: "",
    },
    rangeDays: {
      type: Array,
      default: () => [],
    },
    selectedProducers: {
      type: Array,
      default: () => [],
    },
    allSpecialProducers: {
      type: Array,
      default: () => [],
    },
    calibersList: {
      type: Array,
      default: () => [],
    },
    typeCultive: {
      type: [Number, String],
      default: 1,
    },
    typeDestiny: {
      type: [Number, String],
      default: 1,
    },
  },

  data: () => ({
    internalOpen: false,
    generatingTemplate: false,
    parsingFile: false,
    uploadedFile: null,
    parseResult: null,
  }),

  computed: {
    canApplyImport() {
      return (
        this.parseResult &&
        this.parseResult.validRowsCount > 0 &&
        (!this.parseResult.errors || this.parseResult.errors.length === 0)
      );
    },
  },

  watch: {
    value(val) {
      this.internalOpen = val;
      if (val) {
        this.resetState();
      }
    },
    internalOpen(val) {
      this.$emit("input", val);
    },
  },

  methods: {
    formatDateDMY(val) {
      if (!val) return "";
      if (typeof val === "string") {
        const matchYMD = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (matchYMD) return `${matchYMD[3]}/${matchYMD[2]}/${matchYMD[1]}`;
        const matchDMY = val.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
        if (matchDMY) return `${matchDMY[1]}/${matchDMY[2]}/${matchDMY[3]}`;
      }
      try {
        const d = new Date(val);
        if (!isNaN(d.getTime())) {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${day}/${m}/${y}`;
        }
      } catch (e) {}
      return "";
    },

    resetState() {
      this.uploadedFile = null;
      this.parseResult = null;
      this.parsingFile = false;
      this.generatingTemplate = false;
      if (this.$refs.fileInputRef) {
        this.$refs.fileInputRef.value = "";
      }
    },

    closeModal() {
      this.internalOpen = false;
    },

    triggerFileInput() {
      if (this.$refs.fileInputRef) {
        this.$refs.fileInputRef.click();
      }
    },

    clearFile() {
      this.uploadedFile = null;
      this.parseResult = null;
      if (this.$refs.fileInputRef) {
        this.$refs.fileInputRef.value = "";
      }
    },

    formatFileSize(bytes) {
      if (!bytes || isNaN(bytes)) return "0 KB";
      const kb = bytes / 1024;
      if (kb < 1024) return `${kb.toFixed(1)} KB`;
      return `${(kb / 1024).toFixed(2)} MB`;
    },

    getProducerCalibersCount(prdId) {
      if (!this.parseResult || !this.parseResult.calibersByProducer) return 0;
      const dict = this.parseResult.calibersByProducer[prdId];
      return dict ? Object.keys(dict).length : 0;
    },

    // =========================================================================
    // 1. GENERACIÓN Y DESCARGA DE LA PLANTILLA EXCEL
    // =========================================================================
    async downloadTemplate() {
      if (!this.rangeDays || this.rangeDays.length === 0) {
        this.$fun.alert("El rango de fechas no está configurado.", "warning");
        return;
      }

      this.generatingTemplate = true;
      try {
        const workbook = new ExcelJS.Workbook();
        workbook.creator = "SunExpert ERP";
        workbook.created = new Date();

        // ─────────────────────────────────────────────────────────────
        // HOJA 1: MATRIZ DE PRECIOS A LLENAR
        // ─────────────────────────────────────────────────────────────
        const wsMatrix = workbook.addWorksheet("Asignacion_Precios");

        const fixedHeaders = [
          { header: "ID_PRODUCTOR", key: "PrdID", width: 15 },
          { header: "COD_SOCIO", key: "PrdCardCode", width: 16 },
          { header: "NOMBRE_PRODUCTOR", key: "PrdCardName", width: 36 },
          { header: "ID_CALIBRE", key: "CtgID", width: 14 },
          { header: "CALIBRE", key: "CtgDescription", width: 24 },
        ];

        const dateHeaders = this.rangeDays.map((d) => ({
          header: d.dateKey, // Nombre de columna exacto en formato YYYY-MM-DD
          key: d.dateKey,
          width: 14,
        }));

        wsMatrix.columns = [...fixedHeaders, ...dateHeaders];

        // Estilos de la fila de encabezado
        const headerRow = wsMatrix.getRow(1);
        headerRow.height = 26;
        headerRow.eachCell((cell, colNumber) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10 };
          cell.alignment = { vertical: "middle", horizontal: "center" };
          if (colNumber <= 5) {
            // Azul corporativo para metadatos
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E3A8A" } };
          } else {
            // Verde oscuro para fechas
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF065F46" } };
          }
        });

        // Determinar qué productores incluir en la plantilla:
        // Si es precio especial:
        // - Si el usuario ya seleccionó productores, se incluyen exactamente los seleccionados.
        // - Si aún no ha seleccionado ninguno, se incluyen todos los productores especiales activos del catálogo.
        let producersToInclude = [];
        if (this.isSpecialPrice) {
          if (this.selectedProducers && this.selectedProducers.length > 0) {
            producersToInclude = this.selectedProducers;
          } else if (this.allSpecialProducers && this.allSpecialProducers.length > 0) {
            producersToInclude = this.allSpecialProducers;
          }
        } else {
          producersToInclude = [
            { PrdID: "", PrdCardCode: "ESTANDAR", PrdCardName: "PRECIO ESTANDAR GENERAL" },
          ];
        }

        // Si no hay calibres en la matriz aún, generar calibres de referencia
        const calibers = (this.calibersList && this.calibersList.length > 0)
          ? this.calibersList
          : [
              { CtgID: 1, CtgDescription: "Calibre Ejemplo 1" },
              { CtgID: 2, CtgDescription: "Calibre Ejemplo 2" },
            ];

        // Llenar las filas precargadas
        producersToInclude.forEach((prd) => {
          calibers.forEach((cal) => {
            const rowData = {
              PrdID: prd.PrdID || "",
              PrdCardCode: prd.PrdCardCode || "",
              PrdCardName: prd.PrdCardName || "",
              CtgID: cal.CtgID,
              CtgDescription: cal.CtgDescription,
            };

            // Precargar 0.00 en cada fecha
            this.rangeDays.forEach((d) => {
              rowData[d.dateKey] = 0.00;
            });

            const row = wsMatrix.addRow(rowData);

            // Celdas fijas informativas
            for (let i = 1; i <= 5; i++) {
              const cell = row.getCell(i);
              cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF8FAFC" } };
              cell.font = { size: 9 };
            }

            // Formato monetario para las columnas de precios
            this.rangeDays.forEach((d, idx) => {
              const colIdx = fixedHeaders.length + idx + 1;
              const cell = row.getCell(colIdx);
              cell.numFmt = "#,##0.00";
              cell.alignment = { horizontal: "right" };
            });
          });
        });

        // ─────────────────────────────────────────────────────────────
        // HOJA 2: CATÁLOGO DE PRODUCTORES ESPECIALES (CONSULTA OFICIAL)
        // ─────────────────────────────────────────────────────────────
        if (this.isSpecialPrice && this.allSpecialProducers && this.allSpecialProducers.length > 0) {
          const wsCatalog = workbook.addWorksheet("Catalogo_Productores");
          wsCatalog.columns = [
            { header: "ID_PRODUCTOR", key: "PrdID", width: 16 },
            { header: "CODIGO_CARNE", key: "PrdCardCode", width: 18 },
            { header: "NOMBRE_COMPLETO", key: "PrdCardName", width: 42 },
            { header: "DOCUMENTO_DNI_RUC", key: "Doc", width: 22 },
          ];

          const catHeader = wsCatalog.getRow(1);
          catHeader.height = 24;
          catHeader.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10 };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF475569" } };
            cell.alignment = { vertical: "middle", horizontal: "center" };
          });

          this.allSpecialProducers.forEach((p) => {
            wsCatalog.addRow({
              PrdID: p.PrdID,
              PrdCardCode: p.PrdCardCode || "",
              PrdCardName: p.PrdCardName || "",
              Doc: p.DocumentNumber || p.PrsDocumentNumber || "",
            });
          });
        }

        // Descargar el archivo generado
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        const fileName = `Plantilla_Precios_${this.isSpecialPrice ? "Especial" : "Estandar"}_${this.dateBegin}_al_${this.dateEnd}.xlsx`;
        link.download = fileName;
        link.click();

        this.$fun.alert("Plantilla Excel generada y descargada exitosamente.", "success");
      } catch (err) {
        console.error("Error al generar plantilla Excel:", err);
        this.$fun.alert("Ocurrió un error al generar la plantilla Excel.", "error");
      } finally {
        this.generatingTemplate = false;
      }
    },

    // =========================================================================
    // 2. LECTURA Y VALIDACIÓN DEL ARCHIVO EXCEL SUBIDO
    // =========================================================================
    async onFileSelected(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;
      const file = files[0];
      this.uploadedFile = file;
      this.parseResult = null;
      this.parsingFile = true;

      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(e.target.result);

            const ws = workbook.getWorksheet("Asignacion_Precios") || workbook.worksheets[0];
            if (!ws) {
              this.$fun.alert("No se encontró la hoja de datos en el archivo Excel.", "error");
              this.parsingFile = false;
              return;
            }

            // Mapear encabezados de la fila 1
            const headers = ws.getRow(1).values.slice(1).map((h) => (h ? h.toString().trim() : ""));
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            const dateColumns = [];

            headers.forEach((h, colIdx) => {
              if (dateRegex.test(h)) {
                dateColumns.push({ colIndex: colIdx + 1, dateKey: h });
              }
            });

            if (dateColumns.length === 0) {
              this.parseResult = {
                errors: ["El archivo no contiene columnas con fechas en formato YYYY-MM-DD (ej: 2026-10-06)."],
                validRowsCount: 0,
                producers: [],
                calibersByProducer: {},
                dateColumns: [],
              };
              this.parsingFile = false;
              return;
            }

            const producersMap = {};
            const calibersByProducer = {};
            const standardCalibers = [];
            const errors = [];
            let validRowsCount = 0;

            // Mapa para validar productores si es precio especial
            const allSpecialMap = new Map();
            (this.allSpecialProducers || []).forEach((p) => {
              allSpecialMap.set(p.PrdID, p);
            });

            ws.eachRow({ includeEmpty: false }, (row, rowNumber) => {
              if (rowNumber === 1) return; // Fila de cabecera

              const rawPrdId = row.getCell(1).value;
              const prdCode = (row.getCell(2).value || "").toString().trim();
              const prdName = (row.getCell(3).value || "").toString().trim();
              const rawCtgId = row.getCell(4).value;
              const ctgDesc = (row.getCell(5).value || "").toString().trim();

              const ctgId = parseInt(rawCtgId, 10);
              if (!ctgId || isNaN(ctgId) || ctgId <= 0) {
                errors.push(`Fila ${rowNumber}: ID_CALIBRE inválido (${rawCtgId}).`);
                return;
              }

              if (this.isSpecialPrice) {
                const prdId = parseInt(rawPrdId, 10);
                if (!prdId || isNaN(prdId) || prdId <= 0) {
                  errors.push(`Fila ${rowNumber}: Falta el ID_PRODUCTOR para precio especial.`);
                  return;
                }

                // Verificar si existe en el maestro
                const matchedPrd = allSpecialMap.get(prdId);
                const finalName = matchedPrd ? matchedPrd.PrdCardName : (prdName || `Productor ${prdId}`);
                const finalCode = matchedPrd ? matchedPrd.PrdCardCode : (prdCode || `PRD-${prdId}`);

                if (!producersMap[prdId]) {
                  producersMap[prdId] = {
                    PrdID: prdId,
                    PrdCardName: finalName,
                    PrdCardCode: finalCode,
                    DocumentNumber: matchedPrd ? (matchedPrd.DocumentNumber || matchedPrd.PrsDocumentNumber) : "",
                  };
                  calibersByProducer[prdId] = {};
                }

                if (!calibersByProducer[prdId][ctgId]) {
                  calibersByProducer[prdId][ctgId] = {
                    CtgID: ctgId,
                    CtgDescription: ctgDesc || `Calibre ${ctgId}`,
                    CategoryGroup: "Mango Exportación",
                    daysPrice: {},
                    MpdIDs: {},
                  };
                }

                dateColumns.forEach((dc) => {
                  let cellVal = row.getCell(dc.colIndex).value;
                  if (cellVal && typeof cellVal === "object" && cellVal.result !== undefined) {
                    cellVal = cellVal.result;
                  }
                  let num = parseFloat(cellVal);
                  if (isNaN(num) || num < 0) num = 0;
                  calibersByProducer[prdId][ctgId].daysPrice[dc.dateKey] = num;
                });

                validRowsCount++;
              } else {
                // Modo Estándar
                let existingCal = standardCalibers.find((c) => c.CtgID === ctgId);
                if (!existingCal) {
                  existingCal = {
                    CtgID: ctgId,
                    CtgDescription: ctgDesc || `Calibre ${ctgId}`,
                    CategoryGroup: "Mango Exportación",
                    daysPrice: {},
                    MpdIDs: {},
                  };
                  standardCalibers.push(existingCal);
                }

                dateColumns.forEach((dc) => {
                  let cellVal = row.getCell(dc.colIndex).value;
                  if (cellVal && typeof cellVal === "object" && cellVal.result !== undefined) {
                    cellVal = cellVal.result;
                  }
                  let num = parseFloat(cellVal);
                  if (isNaN(num) || num < 0) num = 0;
                  existingCal.daysPrice[dc.dateKey] = num;
                });

                validRowsCount++;
              }
            });

            this.parseResult = {
              errors,
              validRowsCount,
              producers: Object.values(producersMap),
              calibersByProducer,
              standardCalibers,
              dateColumns,
            };
          } catch (err) {
            console.error("Error al procesar archivo Excel:", err);
            this.parseResult = {
              errors: ["Ocurrió un error al leer el archivo Excel: " + err.message],
              validRowsCount: 0,
              producers: [],
              calibersByProducer: {},
              dateColumns: [],
            };
          } finally {
            this.parsingFile = false;
          }
        };

        reader.readAsArrayBuffer(file);
      } catch (err) {
        console.error("Error FileReader:", err);
        this.parsingFile = false;
        this.$fun.alert("Error al cargar el archivo seleccionado.", "error");
      }
    },

    // =========================================================================
    // 3. APLICAR DATOS IMPORTADOS A LA VISTA PRINCIPAL
    // =========================================================================
    applyImport() {
      if (!this.canApplyImport) return;

      this.$emit("imported", {
        isSpecialPrice: this.isSpecialPrice,
        producers: this.parseResult.producers,
        calibersByProducer: this.parseResult.calibersByProducer,
        standardCalibers: this.parseResult.standardCalibers,
        dateColumns: this.parseResult.dateColumns,
      });

      this.closeModal();
    },
  },
};
</script>

<style scoped>
.border-light-bottom {
  border-bottom: 1px solid #e2e8f0;
}
.border-light {
  border: 1px solid #e2e8f0 !important;
}
.border-dashed {
  border: 2px dashed #cbd5e1 !important;
  transition: all 0.2s ease-in-out;
}
.border-dashed:hover {
  border-color: #2563eb !important;
  background-color: #eff6ff !important;
}
.file-loaded {
  border-color: #10b981 !important;
  background-color: #f0fdf4 !important;
}
.cursor-pointer {
  cursor: pointer;
}
.bg-slate-50 {
  background-color: #f8fafc !important;
}
</style>
